import { useRef, useState } from 'react'
import { Link, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, type MeResponse } from '../api/client'
import { apiErrorText } from '../lib/apiErrorText'
import { approvalWarnings } from './approval'
import {
  approve, choosePhoto, confirmPhrase, editCopy, getCampaign, isDrafting, reject, requestChanges, resume, setLimits, stop, testSend,
  type AudienceComputed, type Detail, type EmailCopy, type FollowUp, type FollowUpTarget, type Package, type PhotoPick,
  type Report, type Version,
} from './api'
import { easternInput, easternToIso, eventDetail, eventLabel, firstLine, fmtEastern, pauseLabel, targetLabel } from './format'
import { cdHref, groupHref, titleName } from './nav'
import { canApprove, limitsControl } from './limits'
import { costLabel, formatCents, parseDollars, pnlRows, sideBySide, type PnL } from './pnl'
import { area, btn, btnPrimary, card, Dialog, ErrorLine, focus, h2, inp, lbl, StatusChip } from './ui'

const LIVE = ['approved', 'running', 'paused']

/** One desk campaign: the package to review (audience, email, reason first), then money, actions,
 *  results and the timeline. Polls while the agent is still drafting. */
export function CampaignPage() {
  const id = Number(useParams().id)
  const qc = useQueryClient()
  const key = ['campaign-desk', 'campaign', id]
  const q = useQuery({
    queryKey: key,
    queryFn: () => getCampaign(id),
    enabled: Number.isFinite(id),
    refetchInterval: (query) => (isDrafting(query.state.data?.campaign.status) ? 5000 : false),
  })
  const [dialog, setDialog] = useState<null | 'approve' | 'changes' | 'reject' | 'stop' | 'resume'>(null)
  const [editing, setEditing] = useState(false)
  const [notice, setNotice] = useState<string | null>(null)
  const { data: me } = useQuery<MeResponse>({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), staleTime: 60_000, retry: false })

  const onDone = (d: Detail, msg?: string) => {
    qc.setQueryData(key, d)
    qc.invalidateQueries({ queryKey: ['campaign-desk', 'list'] })
    setDialog(null)
    setEditing(false)
    setNotice(msg ?? null)
  }

  const limits = useMutation({
    mutationFn: (value: boolean) => setLimits(id, value),
    onSuccess: (x) => onDone(x, x.campaign.ignore_limits
      ? 'Contact limits switched off. The agent is drafting it again for the larger audience.'
      : 'Contact limits switched back on. The agent is drafting it again.'),
    onError: (e) => setNotice(apiErrorText(e, true)),
  })

  const test = useMutation({
    mutationFn: () => testSend(id),
    onSuccess: () => setNotice('Test email sent to you.'),
    onError: (e) => setNotice(apiErrorText(e, true)),
  })

  if (q.isPending) return <p className="text-paper-faint">Loading…</p>
  if (q.error || !q.data) return <ErrorLine text={apiErrorText(q.error)} />
  const d = q.data
  const dc = d.campaign
  const pkg = d.version?.package ?? null
  const computed = pkg?.computed ?? {}
  const status = dc.status

  return (
    <div className="mx-auto flex max-w-5xl flex-col gap-5">
      <header className="flex flex-col gap-2">
        <Link to={cdHref()} className={`self-start text-[13px] text-amber-glow underline underline-offset-2 ${focus}`}>All campaigns</Link>
        <div className="flex flex-wrap items-center gap-3">
          <h1 className="font-serif text-[26px] font-medium tracking-tight">{firstLine(dc.brief) || `Campaign #${dc.id}`}</h1>
          <StatusChip status={status} />
        </div>
        <p className="text-[13px] text-paper-dim">
          {titleName(dc.publication_slug)} · #{dc.id} · brief by {dc.created_by}
          {d.version && <> · version {d.version.version}</>}
          {dc.promo_code && <> · code <strong className="font-mono text-paper">{dc.promo_code}</strong></>}
        </p>
        {dc.brief.includes('\n') && <p className="whitespace-pre-wrap text-[13px] text-paper-dim">{dc.brief}</p>}
        {dc.group_id != null && (
          <Link to={groupHref(dc.group_id)} className={`self-start text-[13px] text-amber-glow underline underline-offset-2 ${focus}`}>
            Part of an every-title brief: see all its campaigns
          </Link>
        )}
      </header>

      <LimitsBanner dc={dc} role={me?.role} busy={limits.isPending} onChange={(v) => limits.mutate(v)} />

      <StatusBanner d={d} />
      {notice && <p role="status" className="rounded-lg border border-ink-400 bg-ink-700 px-4 py-2 text-[13px]">{notice}</p>}
      {d.version && !isDrafting(status) && <AgentReply v={d.version} />}

      {pkg && (
        <>
          <AudienceCard audience={computed.audience} frozen={d.frozen_count} live={LIVE.includes(status) || status === 'completed'} />
          <EmailCard pkg={pkg} />
          {d.version && (
            <PhotoCard id={id} version={d.version.version} pkg={pkg} canChange={status === 'ready'}
                       onDone={(x, msg) => onDone(x, msg)} />
          )}
          <section className={card}>
            <h2 className={h2}>Why this campaign</h2>
            <p className="mt-2 whitespace-pre-wrap text-[15px] leading-relaxed">{pkg.reason}</p>
          </section>
          <Strip pkg={pkg} budgetCents={dc.budget_cents ?? pkg.budget_cents} />
          {pkg.risks.length > 0 && (
            <details className={card}>
              <summary className={`cursor-pointer text-[13px] font-semibold ${focus}`}>Risks ({pkg.risks.length})</summary>
              <ul className="mt-2 list-disc pl-5 text-[13px] text-paper-dim">
                {pkg.risks.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            </details>
          )}
        </>
      )}

      <section className="flex flex-wrap gap-3">
        {status === 'ready' && pkg && (
          <>
            <button type="button" className={btnPrimary} disabled={!canApprove(me?.role, dc)}
                    title={canApprove(me?.role, dc) ? undefined : 'Admins approve campaigns with limits off'}
                    onClick={() => setDialog('approve')}>Approve…</button>
            {!canApprove(me?.role, dc) && <span className="self-center text-[13px] text-paper-faint">Admins approve campaigns with limits off</span>}
            <button type="button" className={btn} onClick={() => setDialog('changes')}>Ask for changes</button>
            <button type="button" className={btn} onClick={() => setEditing((v) => !v)} aria-expanded={editing}>Edit copy</button>
          </>
        )}
        {status === 'failed' && !dc.campaign_id && <button type="button" className={btn} onClick={() => setDialog('changes')}>Ask for a new draft</button>}
        {['ready', 'failed', 'changes_requested'].includes(status) && (
          <button type="button" className={btn} onClick={() => setDialog('reject')}>Reject…</button>
        )}
        {status === 'paused' && <button type="button" className={btnPrimary} onClick={() => setDialog('resume')}>Resume…</button>}
        {LIVE.includes(status) && <button type="button" className={btn} onClick={() => setDialog('stop')}>Stop…</button>}
        {pkg && (status === 'ready' || LIVE.includes(status)) && (
          <button type="button" className={btn} disabled={test.isPending} onClick={() => test.mutate()}>
            {test.isPending ? 'Sending…' : 'Send me a test'}
          </button>
        )}
      </section>

      {editing && pkg && d.version && (
        <EditCopy id={id} version={d.version.version} pkg={pkg} onCancel={() => setEditing(false)} onDone={(x) => onDone(x, 'Copy saved as a new version.')} />
      )}

      {dc.report && <Results report={dc.report} />}
      <CostsTable d={d} />
      <Timeline d={d} />

      {dialog === 'approve' && pkg && d.version && (
        <ApproveDialog id={id} version={d.version.version} pkg={pkg} budgetCents={dc.budget_cents ?? pkg.budget_cents}
                       onClose={() => setDialog(null)} onDone={(x) => onDone(x, 'Approved and scheduled.')} />
      )}
      {dialog === 'changes' && (
        <NoteDialog title="Ask for changes" label="What should the agent change?" action="Send to the agent"
                    run={(t) => requestChanges(id, t)} onClose={() => setDialog(null)} onDone={(x) => onDone(x, 'Sent back to the agent.')} />
      )}
      {dialog === 'reject' && (
        <NoteDialog title="Reject this campaign" label="Why? (kept on the timeline)" action="Reject"
                    run={(t) => reject(id, t)} onClose={() => setDialog(null)} onDone={(x) => onDone(x, 'Rejected.')} />
      )}
      {dialog === 'stop' && (
        <NoteDialog title="Stop this campaign" label="Why? (kept on the timeline)" action="Stop it"
                    hint="Nothing more goes out: not the first email if it hasn't gone yet, and no follow-ups."
                    run={(t) => stop(id, t)} onClose={() => setDialog(null)} onDone={(x) => onDone(x, 'Stopped.')} />
      )}
      {dialog === 'resume' && (
        <ResumeDialog id={id} budgetCents={dc.budget_cents} spentCents={d.spent_cents} onClose={() => setDialog(null)} onDone={(x) => onDone(x, 'Resumed.')} />
      )}
    </div>
  )
}

function LimitsBanner({ dc, role, busy, onChange }: {
  dc: Detail['campaign']; role: string | undefined; busy: boolean; onChange: (ignore: boolean) => void
}) {
  const c = limitsControl(role, dc)
  if (!dc.ignore_limits && !c.canSwitchOff) return null
  if (!dc.ignore_limits) {
    return (
      <div className="flex flex-wrap items-center gap-3 text-[13px] text-paper-dim">
        <button type="button" className={btn} disabled={busy} onClick={() => onChange(true)}>Ignore the desk's contact limits</button>
        <span>Admins only. The agent drafts it again for the larger audience.</span>
      </div>
    )
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-amber-glow/40 bg-amber-wash px-4 py-3 text-[13px] text-paper">
      <span className="flex-1">
        <strong>Contact limits are off for this campaign</strong>
        {dc.ignore_limits_by && <> · switched off by {dc.ignore_limits_by} {fmtEastern(dc.ignore_limits_at)}</>}.
        {' '}People who unsubscribed, bounced or complained are still left out.
      </span>
      {c.canSwitchOn && <button type="button" className={btn} disabled={busy} onClick={() => onChange(false)}>Switch limits back on</button>}
    </div>
  )
}

function StatusBanner({ d }: { d: Detail }) {
  const dc = d.campaign
  const box = 'rounded-lg border px-4 py-3 text-[13px]'
  if (isDrafting(dc.status)) {
    return (
      <p role="status" className={`${box} border-ink-400 bg-ink-700`}>
        {dc.status === 'changes_requested'
          ? <>Back with the agent for changes: <em>{dc.instruction}</em>. This page updates by itself.</>
          : <>The agent is drafting the package. This page updates by itself every few seconds.</>}
      </p>
    )
  }
  if (dc.status === 'failed') {
    // After approval the failure is the Campaign's (dc.error says why); before it, the draft's.
    const text = dc.campaign_id ? (dc.error ?? 'The campaign could not send.') : `The draft failed: ${dc.error ?? 'no reason given'}.`
    return <p role="alert" className={`${box} border-radar-red/40 text-radar-red`}>{text}</p>
  }
  if (d.campaign_status === 'failed' && LIVE.includes(dc.status)) {
    return (
      <p role="alert" className={`${box} border-radar-red/40 text-radar-red`}>
        Campaign #{dc.campaign_id} failed while sending, so nothing more will go out. The results report follows when the attribution window closes.
      </p>
    )
  }
  if (dc.status === 'paused') {
    return (
      <p role="status" className={`${box} border-amber-glow/40 bg-amber-wash`}>
        {pauseLabel(dc.paused_reason)}.{dc.paused_fire_at && <> The next send was due {fmtEastern(dc.paused_fire_at)}.</>}
      </p>
    )
  }
  if (dc.status === 'stopped') return <p className={`${box} border-ink-400`}>Stopped by {dc.stopped_by} {fmtEastern(dc.stopped_at)}: {dc.stop_reason}</p>
  if (dc.status === 'approved' || dc.status === 'running') {
    return <p className={`${box} border-radar-green/40`}>Approved by {dc.approved_by} {fmtEastern(dc.approved_at)}{dc.campaign_id && <> · Campaign #{dc.campaign_id}</>}.</p>
  }
  return null
}

/** The answer to the operator's last change request, and what the new version changed. Shown at
 *  the top so a request is never met with a page that just quietly reloads. */
function AgentReply({ v }: { v: Version }) {
  if (!v.instruction) return null
  const reply = v.package.reply_to_operator?.trim()
  const changes = v.changes ?? []
  return (
    <section className={`${card} border-amber-glow/40`} aria-labelledby="agent-reply">
      <h2 id="agent-reply" className={h2}>Your change request · version {v.version}</h2>
      <p className="mt-2 text-[13px] text-paper-dim">You asked: <em>{v.instruction}</em></p>
      <p className="mt-2 text-[15px] leading-relaxed">
        {reply || 'The agent made a new draft but did not say what it changed. Check the list below and the email.'}
      </p>
      {changes.length > 0 ? (
        <details className="mt-3" open={changes.length <= 4}>
          <summary className={`cursor-pointer text-[13px] font-semibold ${focus}`}>What changed ({changes.length})</summary>
          <ul className="mt-2 flex flex-col gap-1.5 text-[13px]">
            {changes.map((c, i) => (
              <li key={i}>
                <span className="font-semibold">{c.what}:</span>{' '}
                <span className="text-paper-faint line-through decoration-paper-faint/60">{c.before}</span>{' '}
                <span aria-hidden="true">→</span><span className="sr-only">changed to</span> {c.after}
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <p className="mt-3 text-[13px] text-paper-faint">Nothing in the email, audience or offer changed from version {v.version - 1}.</p>
      )}
    </section>
  )
}

/** The lead photograph: what a model checked it shows, its credit, and the other checked options
 *  the operator can swap in (or remove it). */
function PhotoCard({ id, version, pkg, canChange, onDone }: {
  id: number; version: number; pkg: Package; canChange: boolean; onDone: (d: Detail, msg: string) => void
}) {
  const c = pkg.computed ?? {}
  const current = c.photo ?? null
  const options = (c.photo_options ?? []).filter((o) => o.url !== current?.url)
  const [err, setErr] = useState<string | null>(null)
  const pick = useMutation({
    mutationFn: (url: string | null) => choosePhoto(id, { version, url }),
    onSuccess: (d, url) => onDone(d, url ? 'Photo swapped. Saved as a new version.' : 'Photo removed. Saved as a new version.'),
    onError: (e) => setErr(apiErrorText(e, true)),
  })
  if (!current && !options.length && !c.photo_note && !c.photo_subject) {
    return (
      <section className={card}>
        <h2 className={h2}>Photo</h2>
        <p className="mt-2 text-[13px] text-paper-dim">This email has no photo. Ask for changes to have the agent find one from the archive.</p>
      </section>
    )
  }
  return (
    <section className={`${card} flex flex-col gap-3`}>
      <h2 className={h2}>Photo{c.photo_subject ? <> · looking for “{c.photo_subject}”</> : null}</h2>
      {current ? (
        <div className="flex flex-col gap-3 sm:flex-row">
          <img src={current.url} alt={current.alt} className="w-full max-w-[280px] rounded-lg border border-ink-400 object-cover" />
          <div className="flex flex-col gap-1 text-[13px]">
            <p className="text-[15px]">{current.reason}</p>
            <p className="text-paper-dim">{fitLabel(current.fit)}{current.chosen_by ? ` · chosen by ${current.chosen_by}` : ' · chosen by the desk'}</p>
            <p className="text-paper-dim">Credit: {current.credit ?? 'none found'}{current.source_title ? ` · from “${current.source_title}”` : ''}</p>
            {canChange && (
              <button type="button" className={`${btn} mt-2 self-start`} disabled={pick.isPending} onClick={() => pick.mutate(null)}>Remove photo</button>
            )}
          </div>
        </div>
      ) : (
        <p className="text-[13px] text-paper-dim">{c.photo_note || 'No photo.'}</p>
      )}
      {options.length > 0 && (
        <details>
          <summary className={`cursor-pointer text-[13px] font-semibold ${focus}`}>Other photos checked ({options.length})</summary>
          <ul className="mt-3 grid gap-3 sm:grid-cols-3">
            {options.map((o) => <PhotoOption key={o.url} o={o} canChange={canChange} busy={pick.isPending} onUse={() => pick.mutate(o.url)} />)}
          </ul>
        </details>
      )}
      {err && <ErrorLine text={err} />}
    </section>
  )
}

function PhotoOption({ o, canChange, busy, onUse }: { o: PhotoPick; canChange: boolean; busy: boolean; onUse: () => void }) {
  return (
    <li className="flex flex-col gap-1.5 text-[13px]">
      <img src={o.url} alt={o.alt} loading="lazy" className="aspect-[3/2] w-full rounded-lg border border-ink-400 object-cover" />
      <p>{o.reason}</p>
      <p className="text-paper-faint">{fitLabel(o.fit)}</p>
      {canChange && <button type="button" className={`${btn} self-start`} disabled={busy} onClick={onUse}>Use this photo</button>}
    </li>
  )
}

function fitLabel(fit: number): string {
  return ['Not a fit', 'Related, but not the subject', 'Shows the subject', 'Clearly shows the subject'][fit] ?? ''
}

function AudienceCard({ audience, frozen, live }: { audience: AudienceComputed | undefined; frozen: number; live: boolean }) {
  const [sample, setSample] = useState(false)
  if (!audience) return null
  return (
    <section className={card}>
      <h2 className={h2}>Audience</h2>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span className="font-serif text-[52px] font-medium leading-none tracking-tight">{audience.count.toLocaleString('en-US')}</span>
        <span className="text-paper-dim">people will get the first email</span>
      </div>
      {live && frozen > 0 && <p className="mt-1 text-[13px] text-paper-dim">{frozen.toLocaleString('en-US')} were frozen into the cohort at approval.</p>}
      {audience.excluded.length > 0 && (
        <ul className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-paper-dim">
          {audience.excluded.map((x) => (
            <li key={x.reason}><span className="font-mono text-paper">{x.count.toLocaleString('en-US')}</span> left out: {x.reason.replace(/_/g, ' ')}</li>
          ))}
        </ul>
      )}
      {(audience.limits_ignored ?? []).length > 0 && (
        <ul className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[13px] text-amber-glow">
          {(audience.limits_ignored ?? []).map((x) => (
            <li key={x.reason}><span className="font-mono">{x.count.toLocaleString('en-US')}</span> included anyway: {x.reason}</li>
          ))}
        </ul>
      )}
      {audience.sample.length > 0 && (
        <div className="mt-3">
          <button type="button" className={`text-[13px] text-amber-glow underline underline-offset-2 ${focus}`} aria-expanded={sample} onClick={() => setSample((v) => !v)}>
            {sample ? 'Hide sample' : 'Show sample'}
          </button>
          {sample && (
            <ul className="mt-2 grid gap-1 text-[13px] sm:grid-cols-2">
              {audience.sample.map((p) => (
                <li key={p.id}>
                  <Link to={`/desk/members/${p.id}`} className={`underline-offset-2 hover:underline ${focus}`}>
                    {[p.first_name, p.last_name].filter(Boolean).join(' ') || `#${p.id}`}
                  </Link>
                  <span className="ml-2 text-paper-faint">{p.email}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

function EmailCard({ pkg }: { pkg: Package }) {
  const preview = pkg.computed?.email_preview
  return (
    <section className={`${card} flex flex-col gap-3`}>
      <h2 className={h2}>The email</h2>
      <div className="flex flex-col gap-1">
        <p className="text-[17px] font-semibold">{preview?.subject ?? pkg.email.subject}</p>
        {pkg.email.alt_subjects.length > 0 && (
          <p className="text-[13px] text-paper-dim">Alternates: {pkg.email.alt_subjects.map((s) => `“${s}”`).join(' · ')}</p>
        )}
        {pkg.email.preheader && <p className="text-[13px] text-paper-faint">Preview text: {pkg.email.preheader}</p>}
        <p className="text-[13px] text-paper-faint">Planned for {fmtEastern(pkg.email.send_at)}</p>
      </div>
      {preview?.html_body ? (
        <iframe title="Email preview" sandbox="" srcDoc={preview.html_body} className="h-[640px] w-full rounded-lg border border-ink-400 bg-white" />
      ) : (
        <p className="text-paper-faint">No preview stored for this version.</p>
      )}
    </section>
  )
}

function PnlTable({ p }: { p: PnL }) {
  return (
    <table className="w-full text-[13px]">
      <tbody>
        {pnlRows(p).map((r) => (
          <tr key={r.label} className={r.below ? 'border-t border-dashed border-ink-400 text-paper-faint' : r.label === 'Net result' ? 'font-semibold' : ''}>
            <td className="py-0.5 pr-4">{r.label}{r.below ? ' (not in net)' : ''}</td>
            <td className="py-0.5 text-right font-mono">{formatCents(r.cents)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  )
}

function Strip({ pkg, budgetCents }: { pkg: Package; budgetCents: number | null }) {
  const pnl = pkg.computed?.pnl
  return (
    <section className={`${card} grid gap-5 md:grid-cols-[auto_auto_1fr]`}>
      <div>
        <h2 className={h2}>Budget</h2>
        <p className="mt-1 font-mono text-[18px]">{budgetCents == null ? '—' : formatCents(budgetCents)}</p>
      </div>
      <div>
        <h2 className={h2}>Expected net</h2>
        {pnl ? (
          <span tabIndex={0} className={`group relative mt-1 inline-block cursor-help font-mono text-[18px] underline decoration-dotted underline-offset-4 ${focus}`}>
            {formatCents(pnl.net_result_cents)}
            <span role="tooltip" className="invisible absolute left-0 top-full z-20 mt-2 w-[300px] rounded-lg border border-ink-400 bg-ink-800 p-3 font-ui text-paper no-underline opacity-0 shadow-lg transition-opacity group-hover:visible group-hover:opacity-100 group-focus:visible group-focus:opacity-100">
              <PnlTable p={pnl} />
            </span>
          </span>
        ) : <p className="mt-1">—</p>}
        <p className="mt-1 text-[12px] text-paper-faint">
          {pkg.expected.conversions} {pkg.expected.plan_id} {pkg.expected.conversions === 1 ? 'order' : 'orders'} expected
        </p>
      </div>
      <div>
        <h2 className={h2}>Follow-ups</h2>
        {pkg.follow_ups.length === 0 ? <p className="mt-1 text-paper-faint">None. One email only.</p> : (
          <ul className="mt-1 flex flex-col gap-0.5 text-[13px]">
            {pkg.follow_ups.map((f) => (
              <li key={f.day_offset}><strong>Day {f.day_offset}</strong> · to {targetLabel(f.target)} · <span className="text-paper-dim">{f.intent}</span></li>
            ))}
          </ul>
        )}
      </div>
    </section>
  )
}

function Results({ report }: { report: Report }) {
  const rows = sideBySide(report.expected ?? {}, report.actual)
  const n = report.narrative
  return (
    <section className={`${card} flex flex-col gap-4`}>
      <h2 className={h2}>Results</h2>
      <div className="overflow-x-auto">
        <table className="w-full max-w-xl text-[13px]">
          <thead className="text-[11px] uppercase tracking-[0.1em] text-paper-faint">
            <tr><th className="py-1 text-left font-bold" /><th className="py-1 text-right font-bold">Expected</th><th className="py-1 text-right font-bold">Actual</th></tr>
          </thead>
          <tbody>
            <tr>
              <td className="py-0.5">Orders</td>
              <td className="py-0.5 text-right font-mono">{report.expected_orders ?? '—'}</td>
              <td className="py-0.5 text-right font-mono">{report.orders}</td>
            </tr>
            {rows.map((r) => (
              <tr key={r.label} className={r.below ? 'border-t border-dashed border-ink-400 text-paper-faint' : r.label === 'Net result' ? 'font-semibold' : ''}>
                <td className="py-0.5 pr-4">{r.label}{r.below ? ' (not in net)' : ''}</td>
                <td className="py-0.5 text-right font-mono">{r.expected == null ? '—' : formatCents(r.expected)}</td>
                <td className="py-0.5 text-right font-mono">{r.actual == null ? '—' : formatCents(r.actual)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid gap-4 text-[13px] md:grid-cols-2">
        <div>
          <h3 className="font-semibold">How the orders were matched</h3>
          <ul className="mt-1 text-paper-dim">
            {Object.entries(report.by_method).map(([m, v]) => (
              <li key={m}>{m === 'promo' ? 'Used the code' : m === 'clickthrough' ? 'Clicked, then bought' : m === 'cohort' ? 'In the cohort, no code or click' : m}: {v.orders} ({formatCents(v.paid_cents)} paid)</li>
            ))}
          </ul>
        </div>
        <div>
          <h3 className="font-semibold">By wave</h3>
          <ul className="mt-1 text-paper-dim">
            {report.by_wave.map((w) => (
              <li key={w.wave}>{w.wave === 0 ? 'First send' : `Day ${w.wave}`}: {w.sent} sent, {w.opened} opened, {w.clicked} clicked</li>
            ))}
          </ul>
        </div>
      </div>
      {n && (
        <div className="flex flex-col gap-2 text-[14px] leading-relaxed">
          <p>{n.summary}</p>
          <p><strong>What worked:</strong> {n.what_worked}</p>
          <p><strong>What I'd change:</strong> {n.what_id_change}</p>
        </div>
      )}
    </section>
  )
}

function CostsTable({ d }: { d: Detail }) {
  const budget = d.campaign.budget_cents
  if (d.costs.length === 0 && budget == null) return null
  const over = budget != null && d.spent_cents > budget
  return (
    <section className={card}>
      <h2 className={h2}>Costs</h2>
      <p className="mt-1 text-[14px]">
        Spent <strong className={`font-mono ${over ? 'text-radar-red' : ''}`}>{formatCents(d.spent_cents)}</strong>
        {budget != null && <> of a <span className="font-mono">{formatCents(budget)}</span> budget</>}
      </p>
      {d.costs.length > 0 && (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-[11px] uppercase tracking-[0.1em] text-paper-faint">
              <tr><th className="py-1 text-left font-bold">When</th><th className="py-1 text-left font-bold">What</th><th className="py-1 text-left font-bold">Detail</th><th className="py-1 text-right font-bold">Amount</th></tr>
            </thead>
            <tbody>
              {d.costs.map((c, i) => (
                <tr key={i} className="border-t border-ink-500">
                  <td className="whitespace-nowrap py-1 pr-3 text-paper-dim">{fmtEastern(c.occurred_at)}</td>
                  <td className="py-1 pr-3">{costLabel(c.kind)}{c.is_estimate ? ' (estimate)' : ''}</td>
                  <td className="py-1 pr-3 text-paper-faint">{c.source}</td>
                  <td className="py-1 text-right font-mono">{formatCents(c.amount_cents)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}

function Timeline({ d }: { d: Detail }) {
  if (d.events.length === 0) return null
  return (
    <section className={card}>
      <h2 className={h2}>Timeline</h2>
      <ol className="mt-2 flex flex-col divide-y divide-ink-500">
        {[...d.events].reverse().map((e, i) => (
          <li key={i} className="flex flex-wrap gap-x-3 py-2 text-[13px]">
            <span className="w-[170px] shrink-0 text-paper-dim">{fmtEastern(e.at)}</span>
            <span className="font-semibold">{eventLabel(e.kind)}</span>
            <span className="text-paper-faint">{e.by}</span>
            {eventDetail(e.detail) && <span className="basis-full pl-0 text-paper-dim md:pl-[182px]">{eventDetail(e.detail)}</span>}
          </li>
        ))}
      </ol>
    </section>
  )
}

function NoteDialog({ title, label, action, hint, run, onClose, onDone }: {
  title: string; label: string; action: string; hint?: string
  run: (text: string) => Promise<Detail>; onClose: () => void; onDone: (d: Detail) => void
}) {
  const [text, setText] = useState('')
  const [error, setError] = useState<string | null>(null)
  const m = useMutation({ mutationFn: () => run(text.trim()), onSuccess: onDone, onError: (e) => setError(apiErrorText(e)) })
  return (
    <Dialog title={title} onClose={onClose} busy={m.isPending} onSubmit={(e) => { e.preventDefault(); if (text.trim() && !m.isPending) m.mutate() }}>
      {hint && <p className="text-[13px] text-paper-dim">{hint}</p>}
      <label className={lbl}>{label}
        <textarea autoFocus value={text} onChange={(e) => setText(e.target.value)} rows={4} maxLength={2000} className={area} required />
      </label>
      <ErrorLine text={error} />
      <div className="flex justify-end gap-3">
        <button type="button" className={btn} onClick={onClose} disabled={m.isPending}>Cancel</button>
        <button type="submit" className={btnPrimary} disabled={!text.trim() || m.isPending}>{action}</button>
      </div>
    </Dialog>
  )
}

function ApproveDialog({ id, version, pkg, budgetCents, onClose, onDone }: {
  id: number; version: number; pkg: Package; budgetCents: number | null; onClose: () => void; onDone: (d: Detail) => void
}) {
  const [budget, setBudget] = useState(budgetCents == null ? '' : (budgetCents / 100).toFixed(2))
  // The agent's planned time, unless that is already (nearly) past: then half an hour from now.
  const [sendAt, setSendAt] = useState(() => {
    const planned = new Date(pkg.email.send_at)
    const soon = new Date(Date.now() + 30 * 60 * 1000)
    return easternInput(planned > soon ? planned : soon)
  })
  const [typed, setTyped] = useState('')
  const [error, setError] = useState<string | null>(null)
  const busy = useRef(false)
  const phrase = useQuery({ queryKey: ['campaign-desk', 'confirm-phrase', id, version], queryFn: () => confirmPhrase(id), staleTime: 0, gcTime: 0 })

  const cents = parseDollars(budget)
  const iso = easternToIso(sendAt)
  const want = phrase.data?.confirm_phrase
  const ready = cents !== null && !!iso && !!want && typed.trim() === want
  const warnings = approvalWarnings(phrase.data, cents, pkg.computed?.pnl?.total_cost_cents)

  const m = useMutation({
    mutationFn: () => approve(id, { version, budget_cents: cents as number, send_at: iso as string, confirm_token: typed.trim() }),
    onSuccess: onDone,
    onError: (e) => { setError(apiErrorText(e)); phrase.refetch() },
    onSettled: () => { busy.current = false },
  })

  return (
    <Dialog title="Approve and schedule" onClose={onClose} busy={m.isPending}
            onSubmit={(e) => { e.preventDefault(); if (!ready || busy.current) return; busy.current = true; setError(null); m.mutate() }}>
      <label className={`${lbl} max-w-[220px]`}>Budget ($)
        <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} className={inp} required aria-invalid={cents === null} />
      </label>
      <label className={`${lbl} max-w-[280px]`}>Send at (Eastern)
        <input type="datetime-local" value={sendAt} onChange={(e) => setSendAt(e.target.value)} className={inp} required />
        <span className="font-normal text-paper-faint">{iso ? fmtEastern(iso) : 'Pick a date and time.'}</span>
      </label>
      <div className="flex flex-col gap-1.5">
        <span className="text-[13px] text-paper-dim">
          {want ? <>Type <strong className="font-mono text-paper">{want}</strong> to confirm. The number is who gets the first email right now.</> : phrase.isPending ? 'Counting the audience…' : ''}
        </span>
        <input value={typed} onChange={(e) => setTyped(e.target.value)} className={`${inp} font-mono`} aria-label="Confirmation" autoComplete="off" disabled={!want} />
      </div>
      {warnings.map((w) => <p key={w} role="status" className="rounded-md bg-amber-wash px-3 py-2 text-[13px] text-amber-glow">{w}</p>)}
      <ErrorLine text={error ?? (phrase.error ? apiErrorText(phrase.error) : null)} />
      <div className="flex justify-end gap-3">
        <button type="button" className={btn} onClick={onClose} disabled={m.isPending}>Cancel</button>
        <button type="submit" className={btnPrimary} disabled={!ready || m.isPending}>{m.isPending ? 'Scheduling…' : 'Approve'}</button>
      </div>
    </Dialog>
  )
}

function ResumeDialog({ id, budgetCents, spentCents, onClose, onDone }: {
  id: number; budgetCents: number | null; spentCents: number; onClose: () => void; onDone: (d: Detail) => void
}) {
  const [budget, setBudget] = useState('')
  const [error, setError] = useState<string | null>(null)
  const cents = budget.trim() ? parseDollars(budget) : null
  const bad = budget.trim() !== '' && cents === null
  const m = useMutation({ mutationFn: () => resume(id, cents), onSuccess: onDone, onError: (e) => setError(apiErrorText(e)) })
  return (
    <Dialog title="Resume sending" onClose={onClose} busy={m.isPending} onSubmit={(e) => { e.preventDefault(); if (!bad && !m.isPending) m.mutate() }}>
      <p className="text-[13px] text-paper-dim">
        Spent {formatCents(spentCents)}{budgetCents != null && <> of {formatCents(budgetCents)}</>}. The budget can only go up.
      </p>
      <label className={`${lbl} max-w-[220px]`}>New budget ($, optional)
        <input inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} className={inp} aria-invalid={bad} />
      </label>
      <ErrorLine text={error} />
      <div className="flex justify-end gap-3">
        <button type="button" className={btn} onClick={onClose} disabled={m.isPending}>Cancel</button>
        <button type="submit" className={btnPrimary} disabled={bad || m.isPending}>Resume</button>
      </div>
    </Dialog>
  )
}

const TARGETS: FollowUpTarget[] = ['non_openers', 'non_clickers', 'clicked_not_bought', 'not_converted']
const splitParas = (s: string): string[] => s.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean)
const joinParas = (ps: string[]): string => ps.join('\n\n')

function EditCopy({ id, version, pkg, onCancel, onDone }: {
  id: number; version: number; pkg: Package; onCancel: () => void; onDone: (d: Detail) => void
}) {
  const [subject, setSubject] = useState(pkg.email.subject)
  const [paras, setParas] = useState(joinParas(pkg.email.paragraphs))
  const [fus, setFus] = useState(pkg.follow_ups.map((f) => ({ ...f, text: joinParas(f.paragraphs) })))
  const [error, setError] = useState<string | null>(null)

  const email: EmailCopy = { ...pkg.email, subject: subject.trim(), paragraphs: splitParas(paras) }
  const followUps: FollowUp[] = fus.map(({ text, ...f }) => ({ ...f, subject: f.subject.trim(), paragraphs: splitParas(text) }))
  const m = useMutation({ mutationFn: () => editCopy(id, { version, email, follow_ups: followUps }), onSuccess: onDone, onError: (e) => setError(apiErrorText(e)) })
  const set = (i: number, patch: Partial<(typeof fus)[number]>) => setFus((xs) => xs.map((x, j) => (j === i ? { ...x, ...patch } : x)))
  const ok = !!email.subject && email.paragraphs.length >= 1 && email.paragraphs.length <= 6
    && followUps.every((f) => f.subject && f.paragraphs.length >= 1 && f.paragraphs.length <= 4)

  return (
    <form className={`${card} flex flex-col gap-4`} onSubmit={(e) => { e.preventDefault(); if (ok && !m.isPending) m.mutate() }}>
      <h2 className={h2}>Edit copy</h2>
      <p className="text-[12px] text-paper-faint">Separate paragraphs with a blank line. Saving makes a new version; the preview and expected P&amp;L are worked out again.</p>
      <label className={lbl}>Subject
        <input value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={120} className={inp} required />
      </label>
      <label className={lbl}>Paragraphs (up to 6)
        <textarea value={paras} onChange={(e) => setParas(e.target.value)} rows={10} className={area} />
      </label>
      {fus.map((f, i) => (
        <fieldset key={i} className="flex flex-col gap-3 rounded-lg border border-ink-500 p-3">
          <legend className="px-1 text-[13px] font-semibold">Follow-up {i + 1}</legend>
          <div className="flex flex-wrap gap-3">
            <label className={`${lbl} w-[110px]`}>Day
              <input type="number" min={2} max={21} value={f.day_offset} onChange={(e) => set(i, { day_offset: Number(e.target.value) })} className={inp} />
            </label>
            <label className={lbl}>Goes to
              <select value={f.target} onChange={(e) => set(i, { target: e.target.value as FollowUpTarget })} className={inp}>
                {TARGETS.map((t) => <option key={t} value={t}>{targetLabel(t)}</option>)}
              </select>
            </label>
          </div>
          <label className={lbl}>Intent
            <input value={f.intent} onChange={(e) => set(i, { intent: e.target.value })} maxLength={300} className={inp} />
          </label>
          <label className={lbl}>Subject
            <input value={f.subject} onChange={(e) => set(i, { subject: e.target.value })} maxLength={120} className={inp} />
          </label>
          <label className={lbl}>Paragraphs (up to 4)
            <textarea value={f.text} onChange={(e) => set(i, { text: e.target.value })} rows={5} className={area} />
          </label>
          <button type="button" className={`${btn} self-start`} onClick={() => setFus((xs) => xs.filter((_, j) => j !== i))}>Remove this follow-up</button>
        </fieldset>
      ))}
      {fus.length < 3 && (
        <button type="button" className={`${btn} self-start`} onClick={() => setFus((xs) => [...xs, {
          day_offset: Math.min(21, (xs[xs.length - 1]?.day_offset ?? 1) + 3), target: 'not_converted', intent: '', subject: '', paragraphs: [], text: '',
        }])}>Add a follow-up</button>
      )}
      {!ok && <ErrorLine text="Each email needs a subject and at least one paragraph (the first email up to 6, follow-ups up to 4)." />}
      <ErrorLine text={error} />
      <div className="flex gap-3">
        <button type="submit" className={btnPrimary} disabled={!ok || m.isPending}>{m.isPending ? 'Saving…' : 'Save as a new version'}</button>
        <button type="button" className={btn} onClick={onCancel} disabled={m.isPending}>Cancel</button>
      </div>
    </form>
  )
}
